import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import appConfig from 'app-config';
import { useTranslator } from '@jutro/locale';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { MLOnDemandDocumentService } from 'gw-capability-gateway';
import { MLAlertGroupComponent } from 'ml-components-common-react';
import { FormModalPopover } from 'ml-components-platform-react';
import { MLTagManagerService } from 'ml-portals-services-react'

import MLDocumentSearchBtns from './components/MLDocumentSearchBtns/MLDocumentSearchBtns'
import MLDocumentRecipients from './components/MLDocumentRecipients/MLDocumentRecipients'

import metadata from './MLCreateEditDocumentsPopup.metadata.json5';
import styles from './MLCreateEditDocumentsPopup.module.scss';
import messages from './MLCreateEditDocumentsPopup.messages';

import { IntlContext } from '@jutro/legacy/locale';

const MLCreateEditDocumentsPopup = (props) => {
    const {
        policyNumber,
        tagServiceFormName,
        ModalService,
        onCancel,
        viewModelService,
        authHeader,
        emailSubTypes
    } = props;

    const { httpSettings } = appConfig;
    const { refreshData } = useDataRefresh();
    const translator = useTranslator();
    const intl = useContext(IntlContext);

    const { onValidate } = useValidation('MLCreateEditDocumentsPopup');

    const createSearchVM = useCallback((data) => {
        return viewModelService.create(data, 'pc', 
            'metlife.edge.capabilities.gateway.ondemanddocs.dto.OnDemandDocumentSearchRequestDTOML'
        );
    }, [viewModelService]);
    const MAX_SEARCH_RESULT_DISPLAY = useMemo(() => 6, []);
    const [search, updateSearch] = useState(createSearchVM({policyNumber}));
    const [potentialRecipients, updatePotentialRecipients] = useState();
    const [searchPaginationInfo, updateSearchPaginationInfo] = useState({
        data: undefined,
        maxSize: MAX_SEARCH_RESULT_DISPLAY
    })
    const [isAddingRecipients, updateIsAddingRecipients] = useState(false);
    const [searchResults, updateSearchResults] = useState();
    const [draft, updateDraft] = useState();
    const [isEsignForm, updateIsEsignForm] = useState(false);
    const [isMultipleSigner, updateIsMultipleSigner] = useState(false);
    const [primarySigner, updatePrimarySigner] = useState();
    const [secondSigner, updateSecondSigner] = useState();
    const [draftEditorSrc, updateDraftEditorSrc] = useState();
    const [searchCopy, updateSearchCopy] = useState();
    const [selectedRows, updateSelectedRows] = useState([]);
    const [canEmailDocument, setCanEmailDocument] = useState(false);

    const multipleSigners = useMemo(() => {
        const ms = { primarySigner };
        if(secondSigner){
            ms.secondSigner = secondSigner;
        }
        return ms;
    }, [primarySigner, secondSigner]);
    const [modalError, updateModalError] = useState({
        isOpen: false,
        title: "",
        message: ""
    });
    const documentSubTypeOptions = useMemo(() => {
        const typekey = viewModelService.productMetadata.get('pc').types.getTypelist('DocumentSubType_ML');
        return typekey.codes.map((code) => {
            return {
                code: code.code,
                name: translator({id: code.name, defaultMessage: code.name})
            }
        })
    }, [translator, viewModelService.productMetadata]);

    const formatDate = useCallback((date) => {
        if(date){
            return intl.formatDate(new Date(date), { year: "numeric", month: "2-digit", day: "2-digit" })
        }
        return '';
    }, [intl]);

    const handleChange = useCallback((value, path) => {
        const newSearch = viewModelService.clone(search);
        _.set(newSearch, path, value);
        updateSearch(newSearch);
        refreshData();
    },[refreshData, search, viewModelService]);

    const shouldShowSearchResults = () => {
        return !!(searchPaginationInfo.data && !isAddingRecipients);
    };

    const canContinueFromSearch = () => {
        return selectedRows.length > 0;
    };

    const canCreateDraft = () => {
        if(isAddingRecipients && !draftEditorSrc){
            if (isMultipleSigner) {
                if (draft.value.chosenRecipients.length) {
                    if (draft.value.chosenRecipients[0].distributionMethod === 'esignature_ml') {
                        return draft.value.chosenRecipients.length === Object.keys(multipleSigners).length;
                    }
                    return true;
                }
                return false;
            }
            return !!draft.value.chosenRecipients.length;
        }
        return false;
    };


    const createDocument = () => {
        // Call this function with the draftEditor to send the update to PC
        document.querySelectorAll("iframe[class*='draft-editor-iframe']")[0].contentWindow.updateReviewCase(true, 'complete');
    };

    const _getCurrentlySelectedForm = () => {
        if(selectedRows.length > 0){
            return searchResults[selectedRows[0]];
        }
    };

    const _handleMultipleSigner = (form) => {
        if (form.isSpouseRequired || form.isExcludedRequired || form.isSecondaryRequired) {
            updateIsMultipleSigner(true);

            MLOnDemandDocumentService.getPrimarySigner(policyNumber, authHeader).then((result) => {
                updatePrimarySigner(result);
            });

            if (form.isSpouseRequired) {
                MLOnDemandDocumentService.getSpouseSigner(policyNumber).then((result) => {
                    updateSecondSigner(result);
                });
            }else{
                updateSecondSigner();
            }
        }else{
            updateIsMultipleSigner(false);
            updatePrimarySigner();
            updateSecondSigner();
        }
    };

    const onSelectionChange = (rows) => {
        updateSelectedRows(rows);
        if(rows.length > 0){
            _handleMultipleSigner(searchResults[rows[0]])
        }
    }

    const continueFromSearch = () => {
        if (tagServiceFormName) {
            MLTagManagerService.sendLinkClickEvent(`${tagServiceFormName}_Continue`);
        }
        updateIsAddingRecipients(true);
        
        const currentlySelected = _getCurrentlySelectedForm();
        const draftData = {
            formID: currentlySelected.formNumber,
            chosenRecipients: []
        };
        const _draft = viewModelService.create(draftData, 'pc', 
            'metlife.edge.capabilities.gateway.ondemanddocs.dto.OnDemandDocumentDraftDTOML'
        );

        updateDraft(_draft);
        
        setCanEmailDocument(emailSubTypes.includes(currentlySelected.documentSubtype));

        updateIsEsignForm(currentlySelected.isEsignForm);
    };

    const performSearch = () => {
        if (tagServiceFormName) {
            const searchType = {};
            if (search.value.keyword) {
                searchType.keyword = search.value.keyword;
            }
            if (search.value.formNumber) {
                searchType.formNumber = search.value.formNumber;
            }
            MLTagManagerService.sendLinkClickEvent(`${tagServiceFormName}_Search`, search.value.policyNumber, searchType);
        }
        const _searchCopy = {...search.value};
        updateSearchCopy(_searchCopy);

        const promise = MLOnDemandDocumentService.getForms(search.value, authHeader);
        ModalService.showProgressDialog(messages.loading, promise);

        promise.then((results) => {
            updateSearchResults(results);
            updateSearchPaginationInfo({
                ...searchPaginationInfo,
                currentPage: 1,
                data: results,
                totalItems: results.length
            });
        }, (error) => {
            console.error(error);
        });
    };

    const resetSearch = () => {
        if (tagServiceFormName) {
            MLTagManagerService.sendLinkClickEvent(`${tagServiceFormName}_Reset`);
        }
        const newSearch = viewModelService.clone(search);
        newSearch.value = { policyNumber: policyNumber }
        updateSearch(newSearch);
        refreshData();
    };    
    
    const _getDraftEditorIframeSrc = (newDraft) => {
        const draftEditorUrl = newDraft.draftEditorURL.value;
        const url = MLOnDemandDocumentService.getDocumentEditorLink(draftEditorUrl)
        const headers = Object.assign({}, authHeader);
        headers.PortalAuthorization = headers.Authorization;

        fetch(url, {
            headers: {
                'PortalAuthorization': authHeader.Authorization,
                'Authorization': authHeader.Authorization,
                'x-gwre-session': sessionStorage.X_GWRE_SESSION
            },
            method: 'GET',
            timeout: httpSettings.timeout
        }).then((response) => {
            if(response.status >= 200 && response.status <= 299){
                response.blob().then((blob) => {
                    const objectURL = URL.createObjectURL(blob);
                    updateDraftEditorSrc(objectURL);
                })
            }else{
                throw response;
            }
        }).catch((error) => {
            console.error('Could not retrieve document editor', error);
            updateModalError({
                isOpen: true,
                title: messages.error,
                message: messages.couldNotCreateDraft
            })
        })

    }

    const createDraft = () => {
        if (tagServiceFormName) {
            MLTagManagerService.sendLinkClickEvent(`${tagServiceFormName}_createDraft`);
        }
        
        const promise = MLOnDemandDocumentService.createDraft(searchCopy, draft.value, authHeader);
        
        ModalService.showProgressDialog(messages.loading, promise);
        promise.then((result) => {
            const newDraft = viewModelService.clone(draft);
            
            newDraft.value = result;
            updateDraft(newDraft); 
            // We'll not get the draft editor iframe url for now. This is temporary.
            _getDraftEditorIframeSrc(newDraft);
        }, (error) => {
            console.error(error);
            
            updateModalError({
                isOpen: true,
                title: messages.error,
                message: messages.couldNotCreateDraft
            })
        });
    };

    const processDocument = (newDraft) => {
        MLOnDemandDocumentService.createDocument(newDraft.value, authHeader).then(() => {
            if (tagServiceFormName) {
                const formValues = {
                    policyNumber: policyNumber
                };
                MLTagManagerService.sendFormCompleteEvent(`${tagServiceFormName}`, formValues);
            }
            onCancel();
        });
    };

    const sendUpdateToPC = useCallback((event) => {
        const eventData = event.data;
        if (eventData.reviewCaseId !== undefined) {
            if (eventData.complete === true) {
                const newDraft = viewModelService.clone(draft);
                newDraft.reviewCaseXml.value = eventData.reviewCaseContent;
                newDraft.reviewCaseID.value = eventData.reviewCaseId;
                updateDraft(newDraft);
                processDocument(newDraft);
            } else {
                MLOnDemandDocumentService.sendDocumentUpdateToPC(
                    eventData.reviewCaseId, 
                    eventData.reviewCaseContent, 
                    eventData.reviewCaseContentWithValidation,
                    draft.formID.value,
                    draft.policyNumber.value,
                    authHeader);
            }
        }
    }, [draft]);

    const listenForUpdatesFromDraftEditor = useCallback(() => {
        console.info("Something has changed in sendUpdateToPC, redoing listeners to keep function updated");
        if(window.addEventListener){
            window.addEventListener('message', sendUpdateToPC, false);
        } else if (window.attachEvent){
            window.attachEvent('onmessage', sendUpdateToPC, false);
        }
    }, [sendUpdateToPC]);

    const destroyListeners = useCallback(() => {
        console.info("Something has changed in sendUpdateToPC, removing old listeners");
        if(window.addEventListener){
            window.removeEventListener('message', sendUpdateToPC, false);
        } else if (window.attachEvent){
            window.detachEvent('onmessage', sendUpdateToPC, false);
        }
    }, [sendUpdateToPC])

    useEffect(() => {
        MLOnDemandDocumentService.getPotentialRecipients(policyNumber, authHeader).then((result) => {
            updatePotentialRecipients(result);
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        listenForUpdatesFromDraftEditor();
        return destroyListeners;
    }, [destroyListeners, listenForUpdatesFromDraftEditor]);

    const updateChosenRecipients = (_chosenRecipients) => {
        const newDraft = viewModelService.clone(draft);
        _.set(newDraft, 'value.chosenRecipients', _chosenRecipients);
        updateDraft(newDraft);
        refreshData();
    }

    const dateCell = (obj, idx, properties) => {
        const value = obj[properties.path];
        const date = formatDate(value)
        return <span title={date} date-read-only>{date}</span>
    }
    
    const documentSubTypeCell = (obj) => {
        const typekey = _.find(documentSubTypeOptions, { code: obj.documentSubtype});
        if(typekey){
            return <span title={typekey.name} data-read-only>{typekey.name}</span>;
        }
        return <span title={obj.documentSubtype} data-read-only>{obj.documentSubtype}</span>;
    }

    const overrideProps = {
        headerContainer: {
            visible: !isAddingRecipients
        },
        formNumberContainer: {
            visible: !isAddingRecipients
        },
        keywordContainer: {
            visible: !isAddingRecipients
        },
        searchFesultsContainer: {
            visible: shouldShowSearchResults()
        },
        searchResultTable: {
            data: searchPaginationInfo.data,
            onSelectionChange: onSelectionChange
        },
        footerSearchBtns: {
            visible: !shouldShowSearchResults() && !isAddingRecipients
        },
        footerResultBtns: {
            visible: shouldShowSearchResults() || isAddingRecipients
        },
        continueBtn: {
            visible: !isAddingRecipients,
            disabled: !canContinueFromSearch()
        },
        createDraftBtn: {
            visible: isAddingRecipients && !draftEditorSrc,
            disabled: !canCreateDraft()
        },
        createDocumentBtn: {
            visible: !!draftEditorSrc
        },
        recipients: {
            visible: isAddingRecipients && !_.get(draft, 'draftEditorURL.value'),
            isMultipleSigner,
            multipleSigners,
            potentialRecipients,
            chosenRecipients: _.get(draft,'value.chosenRecipients', []),
            tagServiceFormName,
            isEsignForm,
            updatePotentialRecipients,
            updateChosenRecipients,
            canEmailDocument
        },
        iFrameContainer: {
            visible: !!draftEditorSrc
        },
        iFrame: {
            src: draftEditorSrc
        },
        alerts: {
            visible: !draftEditorSrc
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            performSearch,
            resetSearch,
            continueFromSearch,
            createDraft,
            createDocument,
            onCancel,
            dateCell,
            documentSubTypeCell
        },
        resolveComponentMap: {
            MLAlertGroupComponent,
            MLDocumentSearchBtns,
            MLDocumentRecipients
        }
    };

    return (
        <>
            <ViewModelForm
                uiProps={metadata.componentContent}
                overrideProps={overrideProps}
                model={search}
                onValueChange={handleChange}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={styles}
                onValidationChange={onValidate}
            />
            { modalError.isOpen && <FormModalPopover
                isOpen={modalError.isOpen}
                onReject={() => updateModalError({
                    isOpen: false,
                    title: "",
                    message: ""
                })}
                title={translator(modalError.title)}
                simpleMessage={translator(modalError.message)}
                actionBtnLabel={translator(messages.close)}
            />}
        </>
    );
}

MLCreateEditDocumentsPopup.propTypes = {
    policy: PropTypes.shape({}),
    policyNumber: PropTypes.string.isRequired
}

export default MLCreateEditDocumentsPopup;